import { useTheme } from "@mui/material";
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar } from "recharts";
import styles from "../AnalystTrends.module.scss";
import clsx from "clsx";
import { useTranslation } from "next-i18next";

const TooltipContent = ({ payload, coordinate, t }: any) => {
    const payloadData = payload[0]?.payload;

    if (!payloadData) return null;

    const { strongBuy, buy, hold, sell, strongSell, name } = payloadData;

    // Calculate arrow position based on cursor position
    // If cursor is in the left half of the chart, show arrow on the right side of tooltip
    // If cursor is in the right half, show arrow on the left side of tooltip
    const chartWidth = 400; // Approximate chart width, adjust as needed
    //const isLeftSide = coordinate?.x < chartWidth / 2;
    //const arrowClass = !isLeftSide ? styles.arrowRight : styles.arrowLeft;
    const segments = 12;

    const pos = coordinate?.x ?? 0;
    const frac = pos / chartWidth; // доля от 0 до 1

    const arrowClass = frac >= 8 / segments ? styles.arrowRight : styles.arrowLeft;
    const strongBuyLabel = t("analytics.analystForecasts.gauges.labels.strongBuy").replace(
        /\n/g,
        " "
    );
    const strongSellLabel = t("analytics.analystForecasts.gauges.labels.strongSell").replace(
        /\n/g,
        " "
    );

    return (
        <div className={clsx(styles.tooltip, arrowClass)}>
            <h1 className={styles.name}>{name}</h1>
            <div className={clsx(styles.row, styles.strongBuy)}>
                {strongBuyLabel}: {strongBuy}
            </div>
            <div className={clsx(styles.row, styles.buy)}>
                {t("buy")}: {buy}
            </div>
            <div className={clsx(styles.row, styles.hold)}>
                {t("hold")}: {hold}
            </div>
            <div className={clsx(styles.row, styles.sell)}>
                {t("sell")}: {sell}
            </div>
            <div className={clsx(styles.row, styles.strongSell)}>
                {strongSellLabel}: {strongSell}
            </div>
        </div>
    );
};

const CustomXAxisTick = ({ x, y, payload, locale }) => {
    const date = new Date(payload.value);
    const month = date.toLocaleString(locale || "en", { month: "short" });
    const year = date.getFullYear();

    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={10} dy={0} textAnchor="middle" fill="#89909C">
                {month}
            </text>
            <text x={0} y={22} dy={0} textAnchor="middle" fill="#89909C">
                {year}
            </text>
        </g>
    );
};

const CustomBarShape = props => {
    const { fill, x, y, width, height } = props;

    return (
        <path
            d={`
        M${x},${y + height}
        L${x},${y + 4}
        Q${x},${y} ${x + 4},${y}
        L${x + width - 4},${y}
        Q${x + width},${y} ${x + width},${y + 4}
        L${x + width},${y + height}
        Z
      `}
            fill={fill}
        />
    );
};

export const RatingChart = ({ data }) => {
    const { palette } = useTheme();
    const { t, i18n } = useTranslation("common");
    return (
        <ResponsiveContainer>
            <BarChart data={data} barCategoryGap={9}>
                <CartesianGrid
                    strokeDasharray="3 0"
                    stroke={palette.mode === "dark" ? "#444854" : "#C8D0D9"}
                    vertical={false}
                />
                <XAxis
                    dataKey="name"
                    stroke={palette.mode === "dark" ? "#89909C" : "#343841"}
                    width={20}
                    tick={props => <CustomXAxisTick {...props} locale={i18n.language} />}
                    interval={0}
                />
                <YAxis width={40} dx={-8} axisLine={false} tickLine={false} />
                <Tooltip content={props => <TooltipContent {...props} t={t} />} cursor={false} />
                <Bar
                    dataKey="sell"
                    stackId="a"
                    fill="#F25555"
                    maxBarSize={19}
                    isAnimationActive={false}
                    activeBar={false}
                />
                <Bar
                    dataKey="hold"
                    stackId="a"
                    fill="#F19500"
                    maxBarSize={19}
                    isAnimationActive={false}
                    activeBar={false}
                />
                <Bar
                    dataKey="buy"
                    stackId="a"
                    fill="#16BB6F"
                    maxBarSize={19}
                    isAnimationActive={false}
                    activeBar={false}
                    shape={<CustomBarShape />}
                />
            </BarChart>
        </ResponsiveContainer>
    );
};

export default RatingChart;
